/** @format */

import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import ActingForBar from '@/views/approvals/Setup/ActingForBar';
import Mainlayout from '../layouts/mainLayout';
import {
  APP_DASHBOARD_URL,
  APP_REPORT_URL,
  APP_UPLOAD_TEMPLATE_URL,
  APP_DEFAULT_URL,
  APP_ACTIVITY_LOGS_URL,
} from '@/AppConfig';
import { lazyLoad } from '@/utils/lazyLoad';
const LeaveExtension = lazyLoad(() => import('@/views/LeaveManagement/LeaveExtension'));
const LeaveProcess = lazyLoad(() => import('@/views/LeaveManagement/LeaveProcess'));

// Activity Logs
const ActivityLogsView = lazyLoad(() => import('@/views/ActivityLogs'));
const ReportMonitorView = lazyLoad(() => import('@/views/ReportMonitor'));
const PlannedPageView = lazyLoad(() => import('@/views/Planned'));
const ModuleSwitchesView = lazyLoad(() => import('@/views/Admin/ModuleSwitches'));
const MasterDataView = lazyLoad(() => import('@/views/MasterData'));
const OrgStructureView = lazyLoad(() => import('@/views/MasterData/OrgStructure'));
const AccountingYearsView = lazyLoad(() => import('@/views/MasterData/AccountingYears'));
const PurchaseRequisitionsView = lazyLoad(() => import('@/views/Procurement/Requisitions'));
const RequisitionEntryView = lazyLoad(() => import('@/views/Procurement/Requisitions/Entry'));
const RequisitionDocView = lazyLoad(() => import('@/views/Procurement/Requisitions/View'));
const GoodsReceiptsView = lazyLoad(() => import('@/views/Procurement/GoodsReceipts'));
const GoodsReceiptEntryView = lazyLoad(() => import('@/views/Procurement/GoodsReceipts/Entry'));
const GoodsReceiptDocView = lazyLoad(() => import('@/views/Procurement/GoodsReceipts/View'));
const PurchaseReturnsView = lazyLoad(() => import('@/views/Procurement/PurchaseReturns'));
const PurchaseReturnEntryView = lazyLoad(() => import('@/views/Procurement/PurchaseReturns/Entry'));
const PurchaseReturnDocView = lazyLoad(() => import('@/views/Procurement/PurchaseReturns/View'));
const SalesInvoicesView = lazyLoad(() => import('@/views/Sales/Invoices'));
const PickListsView = lazyLoad(() => import('@/views/Sales/PickLists'));
const PickListNewView = lazyLoad(() => import('@/views/Sales/PickLists/New'));
const PickListPickView = lazyLoad(() => import('@/views/Sales/PickLists/Pick'));
const PickListPrintView = lazyLoad(() => import('@/views/Sales/PickLists/Print'));
const PrintPreq = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.RequisitionPrint })));
const PrintGr = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.GoodsReceiptPrint })));
const PrintPret = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.PurchaseReturnPrint })));
const PrintSq = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.QuotationPrint })));
const PrintInv = lazyLoad(() => import('@/views/Printing/InvoicePrint'));
const PrintSret = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.SalesReturnPrint })));
const PrintPlc = lazyLoad(() => import('@/views/Printing/DocPrints').then((m) => ({ default: m.PriceChangePrint })));
const SalesInvoiceEntryView = lazyLoad(() => import('@/views/Sales/Invoices/Entry'));
const SalesInvoiceDocView = lazyLoad(() => import('@/views/Sales/Invoices/View'));
const SalesReturnsView = lazyLoad(() => import('@/views/Sales/Returns'));
const SalesReturnEntryView = lazyLoad(() => import('@/views/Sales/Returns/Entry'));
const SalesReturnDocView = lazyLoad(() => import('@/views/Sales/Returns/View'));
const PriceListsView = lazyLoad(() => import('@/views/Sales/PriceLists'));
const EmailAccountsView = lazyLoad(() => import('@/views/MailCenter/EmailAccounts'));
const InvoiceEmailView = lazyLoad(() => import('@/views/MailCenter/InvoiceEmail'));
const QuotationsView = lazyLoad(() => import('@/views/Sales/Quotations'));
const QuotationEntryView = lazyLoad(() => import('@/views/Sales/Quotations/Entry'));
const QuotationDocView = lazyLoad(() => import('@/views/Sales/Quotations/View'));
const PriceChangesView = lazyLoad(() => import('@/views/Sales/PriceChanges'));
const PriceChangeEntryView = lazyLoad(() => import('@/views/Sales/PriceChanges/Entry'));
const PriceChangeDocView = lazyLoad(() => import('@/views/Sales/PriceChanges/View'));
const AccessReviewsView = lazyLoad(() => import('@/views/Admin/AccessReview'));
const OrgChartView = lazyLoad(() => import('@/views/HR/OrgChart'));

// Lazy loaded view components for optimal code splitting
// HR Module
//const EmployeeMaster = lazyLoad(() => import("@/views/HR/employee-master"));
const ModernEmployeeMaster = lazyLoad(
  () => import('@/views/HR/employee-master/ModernEmployeeMaster')
);
const EmployeeDetail = lazyLoad(
  () => import('@/views/HR/employee-master/employee-details')
);
const AddEmployee = lazyLoad(
  () => import('@/views/HR/employee-master/add-employee')
);
const ReportView = lazyLoad(() => import('@/views/HR/reports'));
const ReportNotConfigured = lazyLoad(() => import('@/views/HR/reports/ReportNotConfigured'));
// const ModernReportView = lazyLoad(
//   () => import('@/views/HR/reports/ModernReportView')
// );
//const MyTeams = lazyLoad(() => import("@/views/HR/my-teams"));
const ModernMyTeams = lazyLoad(
  () => import('@/views/HR/my-teams/ModernMyTeams')
);
//const LeaveSettlement = lazyLoad(() => import("@/views/HR/leave-settlement"));
const ModernLeaveSettlement = lazyLoad(
  () => import('@/views/HR/leave-settlement/ModernLeaveSettlement')
);
const Notifications = lazyLoad(() => import('@/views/HR/Notifications'));
const LicenseKeyDetails = lazyLoad(() => import('@/views/HR/License'));
const AllowanceList = lazyLoad(
  () => import('@/views/HR/allowance-master/AllowanceList')
);
const AllowanceFormPage = lazyLoad(
  () => import('@/views/HR/allowance-master/AllowanceFormPage')
);

const PayrollPeriod = lazyLoad(() => import('@/views/HR/payroll-period'));
const AttendanceEntry = lazyLoad(() => import('@/views/HR/attendance-entry'));
const ShiftScheduleList = lazyLoad(() => import('@/views/HR/shift-schedule'));
const EmployeeShift = lazyLoad(() => import('@/views/HR/employee-shift'));
const EmployeeTransfer = lazyLoad(() => import('@/views/HR/employee-transfer'));
const EmployeeTransferFormPage = lazyLoad(
  () => import('@/views/HR/employee-transfer/EmployeeTransferFormPage')
);
const LeaveCategory = lazyLoad(
  () => import('@/views/LeaveManagement/LeaveCategory')
);
const LeaveApplication = lazyLoad(
  () => import('@/views/LeaveManagement/LeaveApplication')
);
const LeaveRejoin = lazyLoad(
  () => import('@/views/LeaveManagement/LeaveRejoin')
);
const TimesheetEntry = lazyLoad(
  () => import('@/views/LeaveManagement/TimesheetEntry')
);
const LoanApplication = lazyLoad(
  () => import('@/views/LoanManagement/LoanApplication')
);
const PunchReport = lazyLoad(() => import('@/views/HR/punch-report'));

// Dashboard Module
// home: role dashboards first for managers, otherwise the personal dashboard (views/Home)
const HomeView = lazyLoad(() => import('@/views/Home'));
const RoleDashboardsView = lazyLoad(() => import('@/views/Admin/RoleDashboards'));
const ReconciliationView = lazyLoad(() => import('@/views/Finance/Reconciliation'));

const MissingPunch = lazyLoad(
  () => import('@/views/dashboard/missingPunch/missingPunch')
);

const JobOpening = lazyLoad(() => import('@/views/dashboard/jobOpening'));
const JobDetail = lazyLoad(
  () => import('@/views/dashboard/components/jobDetails')
);
const NotFoundPage = lazyLoad(
  () => import('@/views/dashboard/components/notFound')
);

// My Account Module
//const MyAccountMaster = lazyLoad(() => import("@/views/myAccount/my-account-master"));
const ModernMyAccount = lazyLoad(
  () => import('@/views/myAccount/my-account-master/ModernMyAccount')
);

// Configuration Module
// const ControlConfig = lazyLoad(
//   () => import('@/views/configurations/controlConfig')
// );
const ModernControlConfig = lazyLoad(
  () => import('@/views/configurations/controlConfig/ModernControlConfig')
);
//const UIConfig = lazyLoad(() => import('@/views/configurations/uiConfig'));
const ModernUIConfig = lazyLoad(
  () => import('@/views/configurations/uiConfig/ModernUIConfig')
);
const MarginSalesTable = lazyLoad(
  () => import('@/views/configurations/uiConfig/reportSample')
);
const ApprovalRulesPage = lazyLoad(() => import('@/views/approvals/Setup/RulesPage'));
const ApprovalDelegationPage = lazyLoad(() => import('@/views/approvals/Setup/DelegationPage'));

// HR Approvals
//const HRApprovals = lazyLoad(() => import("@/views/approvals/HR"));
const ModernHRApprovals = lazyLoad(
  () => import('@/views/approvals/HR/ModernHRApprovals')
);
const ApprovalDetails = lazyLoad(
  () => import('@/views/approvals/HR/approvalDetails')
);
const Approve = lazyLoad(
  () => import('@/views/approvals/HR/ModernHRApprovalList')
);

// Operation Approvals
//const OperationApprovals = lazyLoad(() => import("@/views/approvals/Operation"));
const ModernOperationApprovals = lazyLoad(
  () => import('@/views/approvals/Operation/ModernOperationApprovals')
);
const OperationApproveList = lazyLoad(
  () => import('@/views/approvals/Operation/operationApprovalList')
);
const OpereationApprovalDetails = lazyLoad(
  () => import('@/views/approvals/Operation/approvalDetails')
);

// Finance Approvals
//const FinanceApprovals = lazyLoad(() => import("@/views/approvals/Finance"));
const ModernFinanceApprovals = lazyLoad(
  () => import('@/views/approvals/Finance/ModernFinanceApprovals')
);
const FinanceApproveList = lazyLoad(
  () => import('@/views/approvals/Finance/financeApprovalList')
);
const FinanceApprovalDetails = lazyLoad(
  () => import('@/views/approvals/Finance/approvalDetails')
);
const Promotion = lazyLoad(
  () => import('@/views/promotion/PromotionEntry/index')
);
// Asset Approvals
//const AssetApprovals = lazyLoad(() => import("@/views/approvals/Assets"));
const ModernAssetApprovals = lazyLoad(
  () => import('@/views/approvals/Assets/ModernAssetApprovals')
);
const AssetApproveList = lazyLoad(
  () => import('@/views/approvals/Assets/assetApprovalList')
);
const AssetApprovalDetails = lazyLoad(
  () => import('@/views/approvals/Assets/approvalDetails')
);

// Products & Excel Import
// const ExcelImport = lazyLoad(() => import('@/views/products/Excel Import'));
const ExcelImport = lazyLoad(
  () => import('@/views/products/Excel Import/ModernProductUpload')
);

// Buying/Vendor Module
const SupplierMaster = lazyLoad(() => import('@/views/Buying/Vendor'));
const VendorDetail = lazyLoad(
  () => import('@/views/Buying/Vendor/VendorDetails')
);
const PendingVendorDetail = lazyLoad(
  () => import('@/views/Buying/Vendor/PendingVendorDetails/VendorDetails')
);
const AddNewBDA = lazyLoad(() => import('@/views/Buying/Vendor/AddVendor'));
const SupplierList = lazyLoad(() => import('@/views/Buying/Listing'));
const SupplierDetails = lazyLoad(() => import('@/views/Buying/Listing/detail'));

// Excel Uploads
const MobileBillUpload = lazyLoad(
  () => import('@/views/excelUploads/BillPayment/index')
);
const FileConfig = lazyLoad(() => import('@/views/excelUploads/UiConfig'));

// Purchase & Finance Entry
const UploadPurchaseEntry = lazyLoad(
  () => import('@/views/Purchase/PurchaseEntry')
  // () => import('@/views/Purchase/PurchaseEntry/ModernPurchaseEntry')
);
const UploadFinancialEntry = lazyLoad(
  () => import('@/views/Finance/FinanceEntry')
  // () => import('@/views/Finance/FinanceEntry/ModernFinanceEntry')
);
const UploadCustomerItemEntry = lazyLoad(
  () => import('@/views/customer/ItemEntry')
);

// Payslip Module
const PayslipProcess = lazyLoad(() => import('@/views/Payslip/payslipProcess'));
const AddPayslipEntry = lazyLoad(
  () => import('@/views/Payslip/payslipProcess/payslipAddNew')
);

// Sales Order Module
const SalesOrders = lazyLoad(() => import('@/views/SalesOrder'));
const AddSalesOrder = lazyLoad(
  () => import('@/views/SalesOrder/AddSalesOrder')
);
const SoPrintView = lazyLoad(
  () => import('@/views/SalesOrder/PrintSO/SoPrintView')
);

// Purchase Order Module
const PurchaseOrders = lazyLoad(() => import('@/views/Purchase/PurchaseOrder'));
const AddPurchaseOrder = lazyLoad(
  () => import('@/views/Purchase/PurchaseOrder/AddPurchaseOrder')
);
const PoPrintView = lazyLoad(
  () => import('@/views/Purchase/PurchaseOrder/PrintPO/PoPrintView')
);
const PoDetailsView = lazyLoad(
  () => import('@/views/Purchase/PurchaseOrder/PoDetailsView')
);
const GrnDetailsView = lazyLoad(
  () => import('@/views/Purchase/PurchaseOrder/GrnDetailsView')
);
const ForeignPurchaseDashboard = lazyLoad(
  () => import('@/views/Purchase/ForeignPurchaseDashboard')
);
const ChequePrintList = lazyLoad(() => import('@/views/Finance/ChequePrint'));
const BankReconciliation = lazyLoad(() => import('@/views/Finance/BankReconciliation'));
const EcomReconciliation = lazyLoad(() => import('@/views/Finance/EcomReconciliation'));
const AccessControl = lazyLoad(() => import('@/views/Admin/AccessControl'));
const KeyboardShortcuts = lazyLoad(() => import('@/views/Admin/KeyboardShortcuts'));
const PrintSetupScreen = lazyLoad(() => import('@/views/Admin/PrintSetup'));
const TxnSetupScreen = lazyLoad(() => import('@/views/Admin/TxnSetup'));
const TxnSetupDetail = lazyLoad(() => import('@/views/Admin/TxnSetup/Detail'));
const DeliverySlots = lazyLoad(() => import('@/views/SalesOrder/DeliverySlots'));
const ChartOfAccounts = lazyLoad(() => import('@/views/Finance/GL/ChartOfAccounts'));
const Analytics = lazyLoad(() => import('@/views/Analytics'));
const Integrations = lazyLoad(() => import('@/views/Integrations'));
const RfqList = lazyLoad(() => import('@/views/Procurement/Rfq'));
const RfqEntry = lazyLoad(() => import('@/views/Procurement/Rfq/Entry'));
const RfqView = lazyLoad(() => import('@/views/Procurement/Rfq/View'));
const RfqQuote = lazyLoad(() => import('@/views/Procurement/Rfq/Quote'));
const ImpList = lazyLoad(() => import('@/views/Procurement/ImportShipments'));
const ImpEntry = lazyLoad(() => import('@/views/Procurement/ImportShipments/Entry'));
const ImpView = lazyLoad(() => import('@/views/Procurement/ImportShipments/View'));
const JournalList = lazyLoad(() => import('@/views/Finance/GL/Journals'));
const ArInvoices = lazyLoad(() => import('@/views/Finance/Receivables/Invoices'));
const ArReceipts = lazyLoad(() => import('@/views/Finance/Receivables/Receipts'));
const ArReceiptEntry = lazyLoad(() => import('@/views/Finance/Receivables/ReceiptEntry'));
const ArReceiptView = lazyLoad(() => import('@/views/Finance/Receivables/ReceiptView'));
const ArStatements = lazyLoad(() => import('@/views/Finance/Receivables/Statements'));
const ArCreditControl = lazyLoad(() => import('@/views/Finance/Receivables/CreditControl'));
const StockEnquiry = lazyLoad(() => import('@/views/Inventory/StockEnquiry'));
const StockTransfers = lazyLoad(() => import('@/views/Inventory/Transfers'));
const ArReceiptPrint = lazyLoad(() => import('@/views/Finance/Receivables/Prints').then((m) => ({ default: m.ReceiptPrint })));
const ArStatementPrint = lazyLoad(() => import('@/views/Finance/Receivables/Prints').then((m) => ({ default: m.StatementPrint })));
const StockTransferPrint = lazyLoad(() => import('@/views/Inventory/Prints').then((m) => ({ default: m.TransferPrint })));
const StockAdjustPrint = lazyLoad(() => import('@/views/Inventory/Prints').then((m) => ({ default: m.AdjustmentPrint })));
const CountSheetPrint = lazyLoad(() => import('@/views/Inventory/Prints').then((m) => ({ default: m.CountSheetPrint })));
const StockTransferNew = lazyLoad(() => import('@/views/Inventory/Transfers/New'));
const StockTransferView = lazyLoad(() => import('@/views/Inventory/Transfers/View'));
const StockAdjustments = lazyLoad(() => import('@/views/Inventory/Adjustments'));
const StockAdjustNew = lazyLoad(() => import('@/views/Inventory/Adjustments/New'));
const StockAdjustView = lazyLoad(() => import('@/views/Inventory/Adjustments/View'));
const StockCounts = lazyLoad(() => import('@/views/Inventory/StockCount'));
const StockCountView = lazyLoad(() => import('@/views/Inventory/StockCount/Count'));
const Replenishment = lazyLoad(() => import('@/views/Inventory/Replenishment'));
const BinsPage = lazyLoad(() => import('@/views/Inventory/Bins'));
const BinLabels = lazyLoad(() => import('@/views/Inventory/Bins/Labels'));
const PutawayPage = lazyLoad(() => import('@/views/Inventory/Putaway'));
const JournalEntry = lazyLoad(() => import('@/views/Finance/GL/Journals/Entry'));
const JournalView = lazyLoad(() => import('@/views/Finance/GL/Journals/View'));
const JournalPrint = lazyLoad(() => import('@/views/Finance/GL/Journals/Print'));
const PeriodClose = lazyLoad(() => import('@/views/Finance/GL/PeriodClose'));
const FinStatement = lazyLoad(() => import('@/views/Finance/Statements/StatementPage'));
const ProductMaster = lazyLoad(() => import('@/views/products/ProductMaster'));
const ItemRequests = lazyLoad(() => import('@/views/products/ItemRequests'));
const CompanyDocs = lazyLoad(
  () => import('@/views/Administration/CompanyDocs')
);
const Item360View = lazyLoad(
  () => import('@/views/products/ProductMaster/Item360View')
);
// master forms ported from Oracle Forms (src/views/MasterData)
const CustomerMasterList = lazyLoad(() =>
  import('@/views/MasterData/CustomerMaster').then((m) => ({ default: m.CustomerMasterList }))
);
const CustomerMasterDetail = lazyLoad(() =>
  import('@/views/MasterData/CustomerMaster').then((m) => ({ default: m.CustomerMasterDetail }))
);
const VendorMasterList = lazyLoad(() =>
  import('@/views/MasterData/VendorMaster').then((m) => ({ default: m.VendorMasterList }))
);
const VendorMasterDetail = lazyLoad(() =>
  import('@/views/MasterData/VendorMaster').then((m) => ({ default: m.VendorMasterDetail }))
);
const ChequePrintView = lazyLoad(
  () => import('@/views/Finance/ChequePrint/ChequePrintView')
);

// Sales Listing & Planning
const SalesPlan = lazyLoad(
  () => import('@/views/salesListing/SalesPlan/AddSalesPlan')
);
const CustomerWiseSales = lazyLoad(
  () => import('@/views/salesListing/CustomerWiseSales')
);

// Loan Management
const LoanMaster = lazyLoad(() => import('@/views/LoanManagement/LoanMaster'));
const LoanSettlement = lazyLoad(
  () => import('@/views/LoanManagement/LoanSettlement')
);
const LoanProcess = lazyLoad(
  () => import('@/views/LoanManagement/LoanProcess')
);
const LoanReschedule = lazyLoad(
  () => import('@/views/LoanManagement/LoanReschedule')
);
const LoanChecklist = lazyLoad(
  () => import('@/views/LoanManagement/LoanChecklist')
);
const EmployeeLoanReport = lazyLoad(
  () => import('@/views/LoanManagement/EmployeeLoanReport')
);
const LoanCompareWithFinance = lazyLoad(
  () => import('@/views/LoanManagement/LoanCompareWithFinance')
);
const SalesPlanList = lazyLoad(() => import('@/views/salesListing/SalesPlan'));

// Target Sales
const ZoneTarget = lazyLoad(() => import('@/views/TargetSales/AddTargetSale'));
const TargetSalesList = lazyLoad(() => import('@/views/TargetSales'));

// WPS
const WpsDtl = lazyLoad(() => import('@/views/WPS'));

const MainRoutes = {
  path: '/',
  element: <Mainlayout />,
  children: [
    {
      path: APP_DEFAULT_URL,
      element: (
        <>
          <Helmet>
            <title>Dashboard | ERPS</title>
          </Helmet>
          <HomeView />
        </>
      ),
    },
    {
      path: APP_DASHBOARD_URL,
      element: (
        <>
          <Helmet>
            <title>Dashboard | ERPS</title>
          </Helmet>
          <HomeView />
        </>
      ),
    },
    {
      path: '/finance/reconciliation',
      element: (
        <>
          <Helmet>
            <title>Operations vs Finance | ERPS</title>
          </Helmet>
          <ReconciliationView />
        </>
      ),
    },
    {
      path: '/admin/role-dashboards',
      element: (
        <>
          <Helmet>
            <title>Role Dashboards | ERPS</title>
          </Helmet>
          <RoleDashboardsView />
        </>
      ),
    },
    {
      // a report menu with no report linked: its link stops at /employee/reports/
      path: '/employee/reports',
      element: (
        <>
          <Helmet>
            <title>Reports | ERPS</title>
          </Helmet>
          <ReportNotConfigured problem="UNLINKED" />
        </>
      ),
    },
    {
      path: APP_REPORT_URL,
      element: (
        <>
          <Helmet>
            <title>Reports | ERPS</title>
          </Helmet>
          <ReportView />
        </>
      ),
    },
    {
      path: APP_ACTIVITY_LOGS_URL,
      element: (
        <>
          <Helmet>
            <title>API Audit Log | ERPS</title>
          </Helmet>
          <ActivityLogsView />
        </>
      ),
    },
    {
      path: '/activity-logs',
      element: (
        <>
          <Helmet>
            <title>Activity Log | ERPS</title>
          </Helmet>
          <ActivityLogsView />
        </>
      ),
    },
    {
      path: '/hr/org-chart',
      element: (
        <>
          <Helmet>
            <title>Org Chart | ERPS</title>
          </Helmet>
          <OrgChartView />
        </>
      ),
    },
    {
      path: '/planned/:slug',
      element: (
        <>
          <Helmet>
            <title>Coming soon | ERPS</title>
          </Helmet>
          <PlannedPageView />
        </>
      ),
    },
    {
      path: '/sales/price-change-requests',
      element: (
        <>
          <Helmet>
            <title>Price Change Requests | ERPS</title>
          </Helmet>
          <PriceChangesView />
        </>
      ),
    },
    {
      path: '/sales/price-change-requests/new',
      element: (
        <>
          <Helmet>
            <title>New Price Change Request | ERPS</title>
          </Helmet>
          <PriceChangeEntryView />
        </>
      ),
    },
    {
      path: '/sales/price-change-requests/view/:id',
      element: (
        <>
          <Helmet>
            <title>Price Change Request | ERPS</title>
          </Helmet>
          <PriceChangeDocView />
        </>
      ),
    },
    {
      path: '/config/email-accounts',
      element: (
        <>
          <Helmet>
            <title>Email Accounts | ERPS</title>
          </Helmet>
          <EmailAccountsView />
        </>
      ),
    },
    {
      path: '/sales/invoice-email',
      element: (
        <>
          <Helmet>
            <title>Invoice Email | ERPS</title>
          </Helmet>
          <InvoiceEmailView />
        </>
      ),
    },
    {
      path: '/sales/price-lists',
      element: (
        <>
          <Helmet>
            <title>Price Lists | ERPS</title>
          </Helmet>
          <PriceListsView />
        </>
      ),
    },
    {
      path: '/sales/returns',
      element: (
        <>
          <Helmet>
            <title>Sales Returns | ERPS</title>
          </Helmet>
          <SalesReturnsView />
        </>
      ),
    },
    {
      path: '/sales/returns/new',
      element: (
        <>
          <Helmet>
            <title>New Sales Return | ERPS</title>
          </Helmet>
          <SalesReturnEntryView />
        </>
      ),
    },
    {
      path: '/sales/returns/view/:id',
      element: (
        <>
          <Helmet>
            <title>Sales Return | ERPS</title>
          </Helmet>
          <SalesReturnDocView />
        </>
      ),
    },
    {
      path: '/procurement/requisitions/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Requisition | ERPS</title>
          </Helmet>
          <PrintPreq />
        </>
      ),
    },
    {
      path: '/procurement/goods-receipts/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Goods Receipt | ERPS</title>
          </Helmet>
          <PrintGr />
        </>
      ),
    },
    {
      path: '/procurement/purchase-returns/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Purchase Return | ERPS</title>
          </Helmet>
          <PrintPret />
        </>
      ),
    },
    {
      path: '/sales/quotations/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Quotation | ERPS</title>
          </Helmet>
          <PrintSq />
        </>
      ),
    },
    {
      path: '/sales/invoices/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Invoice | ERPS</title>
          </Helmet>
          <PrintInv />
        </>
      ),
    },
    {
      path: '/sales/returns/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Sales Return | ERPS</title>
          </Helmet>
          <PrintSret />
        </>
      ),
    },
    {
      path: '/sales/price-change-requests/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Price Change Request | ERPS</title>
          </Helmet>
          <PrintPlc />
        </>
      ),
    },
    {
      path: '/sales/quotations',
      element: (
        <>
          <Helmet>
            <title>Quotations | ERPS</title>
          </Helmet>
          <QuotationsView />
        </>
      ),
    },
    {
      path: '/sales/quotations/new',
      element: (
        <>
          <Helmet>
            <title>New Quotation | ERPS</title>
          </Helmet>
          <QuotationEntryView />
        </>
      ),
    },
    {
      path: '/sales/quotations/view/:id',
      element: (
        <>
          <Helmet>
            <title>Quotation | ERPS</title>
          </Helmet>
          <QuotationDocView />
        </>
      ),
    },
    {
      path: '/sales/pick-lists',
      element: (
        <>
          <Helmet>
            <title>Pick Lists | ERPS</title>
          </Helmet>
          <PickListsView />
        </>
      ),
    },
    {
      path: '/sales/pick-lists/new',
      element: (
        <>
          <Helmet>
            <title>New Pick List | ERPS</title>
          </Helmet>
          <PickListNewView />
        </>
      ),
    },
    {
      path: '/sales/pick-lists/print/:id',
      element: (
        <>
          <Helmet>
            <title>Pick List Print | ERPS</title>
          </Helmet>
          <PickListPrintView />
        </>
      ),
    },
    {
      path: '/sales/pick-lists/:id',
      element: (
        <>
          <Helmet>
            <title>Pick List | ERPS</title>
          </Helmet>
          <PickListPickView />
        </>
      ),
    },
    {
      path: '/sales/invoices',
      element: (
        <>
          <Helmet>
            <title>Sales Invoices | ERPS</title>
          </Helmet>
          <SalesInvoicesView />
        </>
      ),
    },
    {
      path: '/sales/invoices/new',
      element: (
        <>
          <Helmet>
            <title>Invoice a Sales Order | ERPS</title>
          </Helmet>
          <SalesInvoiceEntryView />
        </>
      ),
    },
    {
      path: '/sales/invoices/view/:id',
      element: (
        <>
          <Helmet>
            <title>Sales Invoice | ERPS</title>
          </Helmet>
          <SalesInvoiceDocView />
        </>
      ),
    },
    {
      path: '/procurement/purchase-returns',
      element: (
        <>
          <Helmet>
            <title>Purchase Returns | ERPS</title>
          </Helmet>
          <PurchaseReturnsView />
        </>
      ),
    },
    {
      path: '/procurement/purchase-returns/new',
      element: (
        <>
          <Helmet>
            <title>New Purchase Return | ERPS</title>
          </Helmet>
          <PurchaseReturnEntryView />
        </>
      ),
    },
    {
      path: '/procurement/purchase-returns/view/:id',
      element: (
        <>
          <Helmet>
            <title>Purchase Return | ERPS</title>
          </Helmet>
          <PurchaseReturnDocView />
        </>
      ),
    },
    {
      path: '/procurement/goods-receipts',
      element: (
        <>
          <Helmet>
            <title>Goods Receipts | ERPS</title>
          </Helmet>
          <GoodsReceiptsView />
        </>
      ),
    },
    {
      path: '/procurement/goods-receipts/new',
      element: (
        <>
          <Helmet>
            <title>New Goods Receipt | ERPS</title>
          </Helmet>
          <GoodsReceiptEntryView />
        </>
      ),
    },
    {
      path: '/procurement/goods-receipts/draft/:id',
      element: (
        <>
          <Helmet>
            <title>Goods Receipt Draft | ERPS</title>
          </Helmet>
          <GoodsReceiptEntryView />
        </>
      ),
    },
    {
      path: '/procurement/goods-receipts/view/:id',
      element: (
        <>
          <Helmet>
            <title>Goods Receipt | ERPS</title>
          </Helmet>
          <GoodsReceiptDocView />
        </>
      ),
    },
    {
      path: '/procurement/requisitions/new',
      element: (
        <>
          <Helmet>
            <title>New Requisition | ERPS</title>
          </Helmet>
          <RequisitionEntryView />
        </>
      ),
    },
    {
      path: '/procurement/requisitions/draft/:id',
      element: (
        <>
          <Helmet>
            <title>Requisition Draft | ERPS</title>
          </Helmet>
          <RequisitionEntryView />
        </>
      ),
    },
    {
      path: '/procurement/requisitions/view/:id',
      element: (
        <>
          <Helmet>
            <title>Requisition | ERPS</title>
          </Helmet>
          <RequisitionDocView />
        </>
      ),
    },
    {
      path: '/procurement/requisitions',
      element: (
        <>
          <Helmet>
            <title>Purchase Requisitions | ERPS</title>
          </Helmet>
          <PurchaseRequisitionsView />
        </>
      ),
    },
    {
      path: '/accounting-years',
      element: (
        <>
          <Helmet>
            <title>Accounting Years | ERPS</title>
          </Helmet>
          <AccountingYearsView />
        </>
      ),
    },
    {
      path: '/org-structure',
      element: (
        <>
          <Helmet>
            <title>Organization Structure | ERPS</title>
          </Helmet>
          <OrgStructureView />
        </>
      ),
    },
    {
      path: '/master/:key',
      element: (
        <>
          <Helmet>
            <title>Master Data | ERPS</title>
          </Helmet>
          <MasterDataView />
        </>
      ),
    },
    {
      path: '/config/module-switches',
      element: (
        <>
          <Helmet>
            <title>Module Switches | ERPS</title>
          </Helmet>
          <ModuleSwitchesView />
        </>
      ),
    },
    {
      path: '/report-monitor',
      element: (
        <>
          <Helmet>
            <title>Report Monitor | ERPS</title>
          </Helmet>
          <ReportMonitorView />
        </>
      ),
    },
    {
      path: '/access-reviews',
      element: (
        <>
          <Helmet>
            <title>Access Reviews | ERPS</title>
          </Helmet>
          <AccessReviewsView />
        </>
      ),
    },
    {
      path: '/employee',
      element: (
        <>
          <Helmet>
            <title>Employee Master | ERPS</title>
          </Helmet>
          <ModernEmployeeMaster />
        </>
      ),
    },
    {
      path: '/employee/my-teams',
      element: (
        <>
          <Helmet>
            <title>My Team | ERPS</title>
          </Helmet>
          <ModernMyTeams />
        </>
      ),
    },
    {
      path: '/employee/leave-settlement',
      element: (
        <>
          <Helmet>
            <title>Leave Settlement | ERPS</title>
          </Helmet>
          <ModernLeaveSettlement />
        </>
      ),
    },
    {
      path: '/employee/allowance',
      element: (
        <>
          <Helmet>
            <title>Allowance Master | ERPS</title>
          </Helmet>
          <AllowanceList />
        </>
      ),
    },
    {
      path: '/employee/allowance/add',
      element: (
        <>
          <Helmet>
            <title>Add Allowance | ERPS</title>
          </Helmet>
          <AllowanceFormPage />
        </>
      ),
    },
    {
      path: '/employee/allowance/edit/:id',
      element: (
        <>
          <Helmet>
            <title>Edit Allowance | ERPS</title>
          </Helmet>
          <AllowanceFormPage />
        </>
      ),
    },

    {
      path: '/hr/shift-schedule',
      element: (
        <>
          <Helmet>
            <title>Shift Schedule | ERPS</title>
          </Helmet>
          <ShiftScheduleList />
        </>
      ),
    },
    {
      path: '/employee/allowance/view/:id',
      element: (
        <>
          <Helmet>
            <title>View Allowance | ERPS</title>
          </Helmet>
          <AllowanceFormPage isViewOnly={true} />
        </>
      ),
    },
    {
      path: '/employee/employee-detail',
      element: (
        <>
          <Helmet>
            <title>Employee Detail</title>
          </Helmet>
          <EmployeeDetail />
        </>
      ),
    },
    {
      path: '/my-account',
      element: (
        <>
          <Helmet>
            <title>My Account | ERPS</title>
          </Helmet>
          <ModernMyAccount />
        </>
      ),
    },
    {
      path: '/missing-punch',
      element: (
        <>
          <Helmet>
            <title>Missing Punch | ERPS</title>
          </Helmet>
          <MissingPunch />
        </>
      ),
    },

    {
      path: '/job-opening',
      element: (
        <>
          <Helmet>
            <title>Job Openings | ERPS</title>
          </Helmet>
          <JobOpening />
        </>
      ),
    },
    {
      path: '/employee/newEmployee',
      element: (
        <>
          <Helmet>
            <title>Add Employee</title>
          </Helmet>
          <AddEmployee />
        </>
      ),
    },
    {
      path: '/employee/editEmployee',
      element: (
        <>
          <Helmet>
            <title>Edit Employee</title>
          </Helmet>
          <AddEmployee />
        </>
      ),
    },
    {
      path: '/job-opening/details',
      element: (
        <>
          <Helmet>
            <title>Job Details</title>
          </Helmet>
          <JobDetail />
        </>
      ),
    },
    {
      path: '/config/control-config',
      element: (
        <>
          <Helmet>
            <title>Control Config | ERPS</title>
          </Helmet>
          <ModernControlConfig />
        </>
      ),
    },
    {
      path: '/hr/document-approval-setup',
      element: <Navigate to="/approvals/rules" replace />,
    },
    {
      path: '/approvals/rules',
      element: (
        <>
          <Helmet>
            <title>Approval Rules | ERPS</title>
          </Helmet>
          <ApprovalRulesPage />
        </>
      ),
    },
    {
      path: '/approvals/delegation',
      element: (
        <>
          <Helmet>
            <title>Delegation | ERPS</title>
          </Helmet>
          <ApprovalDelegationPage />
        </>
      ),
    },
    {
      path: '/excel-upload/',
      element: (
        <>
          <Helmet>
            <title>Promotion Upload | ERPS</title>
          </Helmet>
          <Promotion />
        </>
      ),
    },
    {
      path: '/config/ui-config',
      element: (
        <>
          <Helmet>
            <title>UI Config | ERPS</title>
          </Helmet>
          <ModernUIConfig />
        </>
      ),
    },
    {
      path: '/config/ui-config/marginSalesTable',
      element: (
        <>
          <Helmet>
            <title>Report</title>
          </Helmet>
          <MarginSalesTable />
        </>
      ),
    },
    // {
    //     path: "/my-account/account-detail",
    //     element: <MyAccountDetail/>,
    // },
    {
      path: '/approvals/hr',
      element: (
        <>
          <Helmet>
            <title>HR Approvals | ERPS</title>
          </Helmet>
          <ActingForBar />
          <ModernHRApprovals />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list/details',
      element: (
        <>
          <Helmet>
            <title>HR Approvals</title>
          </Helmet>
          <ActingForBar />
          <ApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list',
      element: (
        <>
          <Helmet>
            <title>HR Approvals</title>
          </Helmet>
          <ActingForBar />
          <Approve />
        </>
      ),
    },
    {
      path: '/approvals/operation',
      element: (
        <>
          <Helmet>
            <title>Operations Approvals | ERPS</title>
          </Helmet>
          <ActingForBar />
          <ModernOperationApprovals />
        </>
      ),
    },
    {
      path: '/approvals/finance',
      element: (
        <>
          <Helmet>
            <title>Finance Approvals | ERPS</title>
          </Helmet>
          <ActingForBar />
          <ModernFinanceApprovals />
        </>
      ),
    },
    {
      path: '/approvals/assets',
      element: (
        <>
          <Helmet>
            <title>Asset Approvals | ERPS</title>
          </Helmet>
          <ActingForBar />
          <ModernAssetApprovals />
        </>
      ),
    },
    {
      path: '/approvals/assets/asset-approval-list',
      element: (
        <>
          <Helmet>
            <title>Asset Approvals</title>
          </Helmet>
          <ActingForBar />
          <AssetApproveList />
        </>
      ),
    },
    {
      path: '/approvals/finance/finance-approval-list',
      element: (
        <>
          <Helmet>
            <title>Finance Approvals</title>
          </Helmet>
          <ActingForBar />
          <FinanceApproveList />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list',
      element: (
        <>
          <Helmet>
            <title>Operation Approvals</title>
          </Helmet>
          <ActingForBar />
          <OperationApproveList />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/operationapprovaldetails',
      element: (
        <>
          <Helmet>
            <title>Operation Approvals</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/assets/asset-approval-list/assetapprovaldetails',
      element: (
        <>
          <Helmet>
            <title>Asset Approvals</title>
          </Helmet>
          <ActingForBar />
          <AssetApprovalDetails />
        </>
      ),
    },
    {
      path: '/approval/financeapprovaldetails',
      element: (
        <>
          <Helmet>
            <title>Finance Approvals</title>
          </Helmet>
          <FinanceApprovalDetails />
        </>
      ),
    },
    {
      path: '/excel-import',
      element: (
        <>
          <Helmet>
            <title>Product Import | ERPS</title>
          </Helmet>
          <ExcelImport />
        </>
      ),
    },
    // {
    //   path: "/products/productDetail",
    //   element: <ProductDetail />,
    // },
    {
      path: '/approvals/finance/finance-approval-list/approvalDetails',
      element: (
        <>
          <Helmet>
            <title>Finance Approval Details</title>
          </Helmet>
          <ActingForBar />
          <FinanceApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/finance/finance-approval-list/supplierPaymentDtl',
      element: (
        <>
          <Helmet>
            <title>Finance Supplier Payment Details</title>
          </Helmet>
          <ActingForBar />
          <FinanceApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/finance/finance-approval-list/prepaidVoucherDtl',
      element: (
        <>
          <Helmet>
            <title>Finance Prepaid Voucher Details</title>
          </Helmet>
          <ActingForBar />
          <FinanceApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/customerReqRegDtl',
      element: (
        <>
          <Helmet>
            <title>Customer Request Registration</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/supplierAgreementDtl',
      element: (
        <>
          <Helmet>
            <title>Supplier Agreement</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/TemporaryCreditCardRequisitionDtl',
      element: (
        <>
          <Helmet>
            <title>Temporary Credit Card Requisition</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/employee/announcement',
      element: (
        <>
          <Helmet>
            <title>Announcements | ERPS</title>
          </Helmet>
          <Notifications />
        </>
      ),
    },
    {
      path: APP_UPLOAD_TEMPLATE_URL,
      element: (
        <>
          <Helmet>
            <title>File Upload</title>
          </Helmet>
          <MobileBillUpload />
        </>
      ),
    },
    {
      path: '/vendors',
      element: (
        <>
          <Helmet>
            <title>Supplier Master | ERPS</title>
          </Helmet>

          <SupplierMaster />
        </>
      ),
    },
    {
      path: '/loanMgmt/loanMaster',
      element: (
        <>
          <Helmet>
            <title>Loan Master | ERPS</title>
          </Helmet>
          <LoanMaster />
        </>
      ),
    },
    {
      path: '/loan-management/loan-process',
      element: (
        <>
          <Helmet>
            <title>Loan Processing | ERPS</title>
          </Helmet>
          <LoanProcess />
        </>
      ),
    },
    {
      path: '/loan-management/loan-settlement',
      element: (
        <>
          <Helmet>
            <title>Loan Settlement | ERPS</title>
          </Helmet>
          <LoanSettlement />
        </>
      ),
    },
    {
      path: '/loan-management/loan-reschedule',
      element: (
        <>
          <Helmet>
            <title>Loan Rescheduling | ERPS</title>
          </Helmet>
          <LoanReschedule />
        </>
      ),
    },
    {
      path: '/loan-management/loan-checklist',
      element: (
        <>
          <Helmet>
            <title>Loan Checklist | ERPS</title>
          </Helmet>
          <LoanChecklist />
        </>
      ),
    },
    {
      path: '/vendors/editVendor/:id',
      element: (
        <>
          <Helmet>
            <title>Supplier Master</title>
          </Helmet>
          <VendorDetail />
        </>
      ),
    },
    {
      path: '/vendors/vendor-detail/:id',
      element: (
        <>
          <Helmet>
            <title>Supplier Master</title>
          </Helmet>
          <VendorDetail />
        </>
      ),
    },
    {
      path: '/vendors/pendingVendorDetail/:id',
      element: (
        <>
          <Helmet>
            <title>Supplier Master</title>
          </Helmet>
          <PendingVendorDetail />
        </>
      ),
    },
    {
      path: '/vendors/editPendingVendorDetail/:id',
      element: (
        <>
          <Helmet>
            <title>Supplier Master</title>
          </Helmet>
          <PendingVendorDetail />
        </>
      ),
    },
    {
      path: '/file-upload/ui-config/',
      element: (
        <>
          <Helmet>
            <title>UI Config Upload | ERPS</title>
          </Helmet>
          <FileConfig />
        </>
      ),
    },
    {
      path: '/vendors/addNewBDA',
      element: (
        <>
          <Helmet>
            <title>ADD BDA</title>
          </Helmet>
          <AddNewBDA />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/PurchaseRequisitionDtl',
      element: (
        <>
          <Helmet>
            <title>Purchase Requisition Detail</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/StockAdjustmentRequsitionDtl',
      element: (
        <>
          <Helmet>
            <title>Stock Adjustment Requisition Detail</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/WhouseForeignPDSdtl',
      element: (
        <>
          <Helmet>
            <title>Whouse Foreign Porder-Dept Store Details</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/DestroyVoucherDtl',
      element: (
        <>
          <Helmet>
            <title>Destroy Voucher Details</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/finance/finance-approval-list/ServiceInvoiceDtl',
      element: (
        <>
          <Helmet>
            <title>Service Invoice Details</title>
          </Helmet>
          <ActingForBar />
          <FinanceApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/PriceChangeRequisitionFreshDtl',
      element: (
        <>
          <Helmet>
            <title>Price Change Requisition Fresh Details</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/SalesPromotionDtl',
      element: (
        <>
          <Helmet>
            <title>Sales Promotion Details</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/operation/operation-approval-list/SupplierRegReqDtl',
      element: (
        <>
          <Helmet>
            <title>Supplier Registration Request Details</title>
          </Helmet>
          <ActingForBar />
          <OpereationApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list/EmployeeLoanAppicationDtl',
      element: (
        <>
          <Helmet>
            <title>Employee Loan Application Details | ERPS</title>
          </Helmet>
          <ActingForBar />
          <ApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list/EmployeePunchNoteDtl',
      element: (
        <>
          <Helmet>
            <title>Employee Punch Note Details</title>
          </Helmet>
          <ActingForBar />
          <ApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list/EmployeeLeaveExtensionDtl',
      element: (
        <>
          <Helmet>
            <title>Employee Leave Extension Details</title>
          </Helmet>
          <ActingForBar />
          <ApprovalDetails />
        </>
      ),
    },
    {
      path: '/approvals/hr/hr-approval-list/EmployeeTransferDtl',
      element: (
        <>
          <Helmet>
            <title>Employee Transfer Details</title>
          </Helmet>
          <ActingForBar />
          <ApprovalDetails />
        </>
      ),
    },
    {
      path: '/employee/wps/',
      element: (
        <>
          <Helmet>
            <title>WPS | ERPS</title>
          </Helmet>
          <WpsDtl />
        </>
      ),
    },
    {
      path: '/purchase',
      element: (
        <>
          <Helmet>
            <title>Purchase Entry Upload | ERPS</title>
          </Helmet>
          <UploadPurchaseEntry />
        </>
      ),
    },
    // {
    //   path: "/payslip/process/",
    //   element: (
    //     <>
    //       <Helmet>
    //         <title>Payslip Process</title>
    //       </Helmet>
    //       <PayslipEntry />
    //     </>
    //   ),
    // },
    {
      path: '/payslip/entry/',
      element: (
        <>
          <Helmet>
            <title>Payslip Processing | ERPS</title>
          </Helmet>
          <PayslipProcess />
        </>
      ),
    },
    {
      path: '/payslip/entry/newEntry',
      element: (
        <>
          <Helmet>
            <title>Payslip Entry</title>
          </Helmet>
          <AddPayslipEntry />
        </>
      ),
    },

    {
      path: '/hr/payroll-period',
      element: (
        <>
          <Helmet>
            <title>Payroll Periods | ERPS</title>
          </Helmet>
          <PayrollPeriod />
        </>
      ),
    },
    {
      path: '/payroll-period',
      element: <Navigate to="/hr/payroll-period" replace />,
    },

    {
      path: '/hr/attendance-entry',
      element: (
        <>
          <Helmet>
            <title>Attendance Entry</title>
          </Helmet>
          <AttendanceEntry />
        </>
      ),
    },
    {
      path: '/hr/employee-shift',
      element: (
        <>
          <Helmet>
            <title>Employee Shifts | ERPS</title>
          </Helmet>
          <EmployeeShift />
        </>
      ),
    },
    {
      path: '/hr/punch-report',
      element: (
        <>
          <Helmet>
            <title>Punch Report | ERPS</title>
          </Helmet>
          <PunchReport />
        </>
      ),
    },
    {
      path: '/hr/employee-transfer',
      element: (
        <>
          <Helmet>
            <title>Employee Transfer | ERPS</title>
          </Helmet>
          <EmployeeTransfer />
        </>
      ),
    },
    {
      path: '/hr/employee-transfer/new',
      element: (
        <>
          <Helmet>
            <title>New Employee Transfer Request | ERPS</title>
          </Helmet>
          <EmployeeTransferFormPage />
        </>
      ),
    },
    {
      path: '/hr/employee-transfer/edit/:id',
      element: (
        <>
          <Helmet>
            <title>Edit Employee Transfer Request | ERPS</title>
          </Helmet>
          <EmployeeTransferFormPage />
        </>
      ),
    },
    {
      path: '/employee-transfer',
      element: (
        <>
          <Helmet>
            <title>Employee Transfer Request | ERPS</title>
          </Helmet>
          <EmployeeTransfer />
        </>
      ),
    },
    {
      path: '/employee/transfer',
      element: (
        <>
          <Helmet>
            <title>Employee Transfer Request | ERPS</title>
          </Helmet>
          <EmployeeTransfer />
        </>
      ),
    },
    {
      path: '/leave-category',
      element: (
        <>
          <Helmet>
            <title>Leave Category | ERPS</title>
          </Helmet>
          <LeaveCategory />
        </>
      ),
    },
    {
      path: '/leave-management',
      element: (
        <>
          <Helmet>
            <title>Leave Application | ERPS</title>
          </Helmet>
          <LeaveApplication />
        </>
      ),
    },
    {
      path: '/leave-management/leave-category',
      element: (
        <>
          <Helmet>
            <title>Leave Categories | ERPS</title>
          </Helmet>
          <LeaveCategory />
        </>
      ),
    },
    {
      path: '/leave-management/leave-application',
      element: (
        <>
          <Helmet>
            <title>Leave Applications | ERPS</title>
          </Helmet>
          <LeaveApplication />
        </>
      ),
    },
    {
      path: '/leave-management/leave-rejoin',
      element: (
        <>
          <Helmet>
            <title>Leave Rejoining | ERPS</title>
          </Helmet>
          <LeaveRejoin />
        </>
      ),
    },
    {
      path: '/leave-management/timesheet-entry',
      element: (
        <>
          <Helmet>
            <title>Timesheet Entry | ERPS</title>
          </Helmet>
          <TimesheetEntry />
        </>
      ),
    },
    {
      path: '/loan-management/loan-application',
      element: (
        <>
          <Helmet>
            <title>Loan Applications | ERPS</title>
          </Helmet>
          <LoanApplication />
        </>
      ),
    },
    {
      path: '/loan-management/loan-master',
      element: (
        <>
          <Helmet>
            <title>Loan Master | ERPS</title>
          </Helmet>
          <LoanMaster />
        </>
      ),
    },
    {
      path: '/loan-management/loan-process',
      element: (
        <>
          <Helmet>
            <title>Loan Processing | ERPS</title>
          </Helmet>
          <LoanProcess />
        </>
      ),
    },
    {
      path: '/loan-management/loan-reschedule',
      element: (
        <>
          <Helmet>
            <title>Loan Rescheduling | ERPS</title>
          </Helmet>
          <LoanReschedule />
        </>
      ),
    },
    {
      path: '/loan-management/employee-loan-report',
      element: (
        <>
          <Helmet>
            <title>Employee Loan Report | ERPS</title>
          </Helmet>
          <EmployeeLoanReport />
        </>
      ),
    },
    {
      path: '/loan-management/loan-compare-finance',
      element: (
        <>
          <Helmet>
            <title>Loan vs Finance | ERPS</title>
          </Helmet>
          <LoanCompareWithFinance />
        </>
      ),
    },
    {
      path: '/hr/leave-category',
      element: (
        <>
          <Helmet>
            <title>Leave Category | ERPS</title>
          </Helmet>
          <LeaveCategory />
        </>
      ),
    },
    {
      path: '/hr/leave-management',
      element: (
        <>
          <Helmet>
            <title>Leave Category | ERPS</title>
          </Helmet>
          <LeaveCategory />
        </>
      ),
    },
    {
      path: '/leave-process',
      element: (
        <>
          <Helmet>
            <title>Leave Process | ERPS</title>
          </Helmet>
          <LeaveProcess />
        </>
      ),
    },
    {
      path: '/leave-management/leave-process',
      element: (
        <>
          <Helmet>
            <title>Leave Processing | ERPS</title>
          </Helmet>
          <LeaveProcess />
        </>
      ),
    },
    {
      path: '/hr/leave-process',
      element: (
        <>
          <Helmet>
            <title>Leave Process | ERPS</title>
          </Helmet>
          <LeaveProcess />
        </>
      ),
    },
    {
      path: '/leave-extension',
      element: (
        <>
          <Helmet>
            <title>Leave Extension | ERPS</title>
          </Helmet>
          <LeaveExtension />
        </>
      ),
    },
    {
      path: '/leave-management/leave-extension',
      element: (
        <>
          <Helmet>
            <title>Leave Extensions | ERPS</title>
          </Helmet>
          <LeaveExtension />
        </>
      ),
    },
    {
      path: '/leave-management/extend-leave',
      element: (
        <>
          <Helmet>
            <title>Leave Extension | ERPS</title>
          </Helmet>
          <LeaveExtension />
        </>
      ),
    },
    {
      path: '/hr/leave-extension',
      element: (
        <>
          <Helmet>
            <title>Leave Extension | ERPS</title>
          </Helmet>
          <LeaveExtension />
        </>
      ),
    },
    {
      path: '/finance',
      element: (
        <>
          <Helmet>
            <title>Financial Entry Upload | ERPS</title>
          </Helmet>
          <UploadFinancialEntry />
        </>
      ),
    },
    {
      path: '/LicenseKeyDetails',
      element: (
        <>
          <Helmet>
            <title>License Keys | ERPS</title>
          </Helmet>

          <LicenseKeyDetails />
        </>
      ),
    },
    {
      path: '/sales-order/',
      element: (
        <>
          <Helmet>
            <title>Sales Orders | ERPS</title>
          </Helmet>

          <SalesOrders />
        </>
      ),
    },
    {
      path: '/SalesOrder/AddSalesOrder',
      element: (
        <>
          <Helmet>
            <title>Add Sales Order</title>
          </Helmet>

          <AddSalesOrder />
        </>
      ),
    },
    {
      path: '/SalesOrder/EditSalesOrder/:id',
      element: (
        <>
          <Helmet>
            <title>Edit Sales Order</title>
          </Helmet>
          <AddSalesOrder />
        </>
      ),
    },
    {
      path: '/SalesOrder/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Sales Order</title>
          </Helmet>
          <SoPrintView />
        </>
      ),
    },
    {
      path: '/sales-order/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Sales Order</title>
          </Helmet>
          <SoPrintView />
        </>
      ),
    },
    {
      path: '/purchase-order',
      element: (
        <>
          <Helmet>
            <title>Purchase Orders | ERPS</title>
          </Helmet>
          <PurchaseOrders />
        </>
      ),
    },
    {
      path: '/purchase-order/add',
      element: (
        <>
          <Helmet>
            <title>Create Purchase Order</title>
          </Helmet>
          <AddPurchaseOrder />
        </>
      ),
    },
    {
      path: '/purchase-order/edit/:id',
      element: (
        <>
          <Helmet>
            <title>Edit Purchase Order Draft</title>
          </Helmet>
          <AddPurchaseOrder />
        </>
      ),
    },
    {
      path: '/purchase-order/view/:id',
      element: (
        <>
          <Helmet>
            <title>Purchase Order Details</title>
          </Helmet>
          <PoDetailsView />
        </>
      ),
    },
    {
      path: '/goods-receipt/view/:id',
      element: (
        <>
          <Helmet>
            <title>Goods Receipt</title>
          </Helmet>
          <GrnDetailsView />
        </>
      ),
    },
    {
      path: '/purchase-order/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Purchase Order</title>
          </Helmet>
          <PoPrintView />
        </>
      ),
    },
    {
      path: '/foreign-purchase-dashboard',
      element: (
        <>
          <Helmet>
            <title>Foreign Purchase Dashboard | ERPS</title>
          </Helmet>
          <ForeignPurchaseDashboard />
        </>
      ),
    },
    {
      path: '/company-docs',
      element: (
        <>
          <Helmet>
            <title>Company Documents | ERPS</title>
          </Helmet>
          <CompanyDocs />
        </>
      ),
    },
    {
      path: '/item-requests',
      element: (
        <>
          <Helmet>
            <title>Item Requests | ERPS</title>
          </Helmet>
          <ItemRequests />
        </>
      ),
    },
    {
      path: '/finance/journal-entries',
      element: (
        <>
          <Helmet>
            <title>Journal Entries | ERPS</title>
          </Helmet>
          <JournalList />
        </>
      ),
    },
    {
      path: '/finance/journal-entries/new',
      element: (
        <>
          <Helmet>
            <title>New Journal | ERPS</title>
          </Helmet>
          <JournalEntry />
        </>
      ),
    },
    {
      path: '/finance/journal-entries/draft/:id',
      element: (
        <>
          <Helmet>
            <title>Journal Draft | ERPS</title>
          </Helmet>
          <JournalEntry />
        </>
      ),
    },
    {
      path: '/finance/journal-entries/view/:kind/:id',
      element: (
        <>
          <Helmet>
            <title>Journal Voucher | ERPS</title>
          </Helmet>
          <JournalView />
        </>
      ),
    },
    {
      path: '/finance/journal-entries/print/:kind/:id',
      element: (
        <>
          <Helmet>
            <title>Journal Voucher Print | ERPS</title>
          </Helmet>
          <JournalPrint />
        </>
      ),
    },
    {
      path: '/finance/customer-invoices',
      element: (
        <>
          <Helmet>
            <title>Customer Invoices | ERPS</title>
          </Helmet>
          <ArInvoices />
        </>
      ),
    },
    {
      path: '/finance/customer-receipts',
      element: (
        <>
          <Helmet>
            <title>Customer Receipts | ERPS</title>
          </Helmet>
          <ArReceipts />
        </>
      ),
    },
    {
      path: '/finance/customer-receipts/new',
      element: (
        <>
          <Helmet>
            <title>New Customer Receipt | ERPS</title>
          </Helmet>
          <ArReceiptEntry />
        </>
      ),
    },
    {
      path: '/finance/customer-receipts/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Receipt | ERPS</title>
          </Helmet>
          <ArReceiptPrint />
        </>
      ),
    },
    {
      path: '/finance/customer-statements/print',
      element: (
        <>
          <Helmet>
            <title>Print Statement | ERPS</title>
          </Helmet>
          <ArStatementPrint />
        </>
      ),
    },
    {
      path: '/finance/customer-receipts/view/:id',
      element: (
        <>
          <Helmet>
            <title>Customer Receipt | ERPS</title>
          </Helmet>
          <ArReceiptView />
        </>
      ),
    },
    {
      path: '/finance/customer-statements',
      element: (
        <>
          <Helmet>
            <title>Customer Statements | ERPS</title>
          </Helmet>
          <ArStatements />
        </>
      ),
    },
    {
      path: '/inventory/stock-transfers',
      element: (
        <>
          <Helmet>
            <title>Stock Transfers | ERPS</title>
          </Helmet>
          <StockTransfers />
        </>
      ),
    },
    {
      path: '/inventory/stock-transfers/new',
      element: (
        <>
          <Helmet>
            <title>New Stock Transfer | ERPS</title>
          </Helmet>
          <StockTransferNew />
        </>
      ),
    },
    {
      path: '/inventory/stock-transfers/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Stock Transfer | ERPS</title>
          </Helmet>
          <StockTransferPrint />
        </>
      ),
    },
    {
      path: '/inventory/stock-count/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Count Sheet | ERPS</title>
          </Helmet>
          <CountSheetPrint />
        </>
      ),
    },
    {
      path: '/inventory/stock-adjustments/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Stock Adjustment | ERPS</title>
          </Helmet>
          <StockAdjustPrint />
        </>
      ),
    },
    {
      path: '/inventory/stock-transfers/:id',
      element: (
        <>
          <Helmet>
            <title>Stock Transfer | ERPS</title>
          </Helmet>
          <StockTransferView />
        </>
      ),
    },
    {
      path: '/inventory/stock-adjustments',
      element: (
        <>
          <Helmet>
            <title>Stock Adjustments | ERPS</title>
          </Helmet>
          <StockAdjustments />
        </>
      ),
    },
    {
      path: '/inventory/stock-adjustments/new',
      element: (
        <>
          <Helmet>
            <title>New Stock Adjustment | ERPS</title>
          </Helmet>
          <StockAdjustNew />
        </>
      ),
    },
    {
      path: '/inventory/stock-adjustments/:id',
      element: (
        <>
          <Helmet>
            <title>Stock Adjustment | ERPS</title>
          </Helmet>
          <StockAdjustView />
        </>
      ),
    },
    {
      path: '/inventory/stock-count',
      element: (
        <>
          <Helmet>
            <title>Stock Count | ERPS</title>
          </Helmet>
          <StockCounts />
        </>
      ),
    },
    {
      path: '/inventory/stock-count/:id',
      element: (
        <>
          <Helmet>
            <title>Stock Count | ERPS</title>
          </Helmet>
          <StockCountView />
        </>
      ),
    },
    {
      path: '/inventory/replenishment',
      element: (
        <>
          <Helmet>
            <title>Replenishment | ERPS</title>
          </Helmet>
          <Replenishment />
        </>
      ),
    },
    {
      path: '/warehouse/bins',
      element: (
        <>
          <Helmet>
            <title>Warehouses &amp; Bins | ERPS</title>
          </Helmet>
          <BinsPage />
        </>
      ),
    },
    {
      path: '/warehouse/bins/labels',
      element: (
        <>
          <Helmet>
            <title>Bin Labels | ERPS</title>
          </Helmet>
          <BinLabels />
        </>
      ),
    },
    {
      path: '/warehouse/putaway',
      element: (
        <>
          <Helmet>
            <title>Putaway &amp; Picking | ERPS</title>
          </Helmet>
          <PutawayPage />
        </>
      ),
    },
    {
      path: '/inventory/stock-enquiry',
      element: (
        <>
          <Helmet>
            <title>Stock Enquiry | ERPS</title>
          </Helmet>
          <StockEnquiry />
        </>
      ),
    },
    {
      path: '/finance/credit-control',
      element: (
        <>
          <Helmet>
            <title>Credit Control | ERPS</title>
          </Helmet>
          <ArCreditControl />
        </>
      ),
    },
    {
      path: '/finance/period-close',
      element: (
        <>
          <Helmet>
            <title>Period Close | ERPS</title>
          </Helmet>
          <PeriodClose />
        </>
      ),
    },
    {
      path: '/finance/trial-balance',
      element: (
        <>
          <Helmet>
            <title>Trial Balance | ERPS</title>
          </Helmet>
          <FinStatement kind="tb" key="tb" />
        </>
      ),
    },
    {
      path: '/finance/profit-loss',
      element: (
        <>
          <Helmet>
            <title>Profit and Loss | ERPS</title>
          </Helmet>
          <FinStatement kind="pl" key="pl" />
        </>
      ),
    },
    {
      path: '/finance/balance-sheet',
      element: (
        <>
          <Helmet>
            <title>Balance Sheet | ERPS</title>
          </Helmet>
          <FinStatement kind="bs" key="bs" />
        </>
      ),
    },
    {
      path: '/finance/cash-flow',
      element: (
        <>
          <Helmet>
            <title>Cash Flow Statement | ERPS</title>
          </Helmet>
          <FinStatement kind="cf" key="cf" />
        </>
      ),
    },
    {
      path: '/procurement/import-shipments',
      element: (
        <>
          <Helmet>
            <title>Import Shipments | ERPS</title>
          </Helmet>
          <ImpList />
        </>
      ),
    },
    {
      path: '/procurement/import-shipments/new',
      element: (
        <>
          <Helmet>
            <title>New Import Shipment | ERPS</title>
          </Helmet>
          <ImpEntry />
        </>
      ),
    },
    {
      path: '/procurement/import-shipments/edit/:id',
      element: (
        <>
          <Helmet>
            <title>Change Import Shipment | ERPS</title>
          </Helmet>
          <ImpEntry />
        </>
      ),
    },
    {
      path: '/procurement/import-shipments/view/:id',
      element: (
        <>
          <Helmet>
            <title>Import Shipment | ERPS</title>
          </Helmet>
          <ImpView />
        </>
      ),
    },
    {
      path: '/procurement/rfq',
      element: (
        <>
          <Helmet>
            <title>Requests for Quotation | ERPS</title>
          </Helmet>
          <RfqList />
        </>
      ),
    },
    {
      path: '/procurement/rfq/new',
      element: (
        <>
          <Helmet>
            <title>New Request for Quotation | ERPS</title>
          </Helmet>
          <RfqEntry />
        </>
      ),
    },
    {
      path: '/procurement/rfq/edit/:id',
      element: (
        <>
          <Helmet>
            <title>Change Request for Quotation | ERPS</title>
          </Helmet>
          <RfqEntry />
        </>
      ),
    },
    {
      path: '/procurement/rfq/view/:id',
      element: (
        <>
          <Helmet>
            <title>Request for Quotation | ERPS</title>
          </Helmet>
          <RfqView />
        </>
      ),
    },
    {
      path: '/procurement/rfq/view/:id/quote/:supp',
      element: (
        <>
          <Helmet>
            <title>Supplier Quote | ERPS</title>
          </Helmet>
          <RfqQuote />
        </>
      ),
    },
    {
      path: '/system/integrations',
      element: (
        <>
          <Helmet>
            <title>Integrations | ERPS</title>
          </Helmet>
          <Integrations />
        </>
      ),
    },
    {
      path: '/analytics',
      element: (
        <>
          <Helmet>
            <title>Analytics | ERPS</title>
          </Helmet>
          <Analytics />
        </>
      ),
    },
    {
      path: '/finance/chart-of-accounts',
      element: (
        <>
          <Helmet>
            <title>Chart of Accounts | ERPS</title>
          </Helmet>
          <ChartOfAccounts />
        </>
      ),
    },
    {
      path: '/product-master',
      element: (
        <>
          <Helmet>
            <title>Product Master | ERPS</title>
          </Helmet>
          <ProductMaster />
        </>
      ),
    },
    {
      path: '/product-master/view/:code',
      element: (
        <>
          <Helmet>
            <title>Item 360</title>
          </Helmet>
          <Item360View />
        </>
      ),
    },
    {
      path: '/customer-master',
      element: (
        <>
          <Helmet>
            <title>Customer Master | ERPS</title>
          </Helmet>
          <CustomerMasterList />
        </>
      ),
    },
    {
      path: '/customer-master/new',
      element: (
        <>
          <Helmet>
            <title>New Customer</title>
          </Helmet>
          <CustomerMasterDetail />
        </>
      ),
    },
    {
      path: '/customer-master/view/:code',
      element: (
        <>
          <Helmet>
            <title>Customer</title>
          </Helmet>
          <CustomerMasterDetail />
        </>
      ),
    },
    {
      path: '/vendor-master',
      element: (
        <>
          <Helmet>
            <title>Vendor Master | ERPS</title>
          </Helmet>
          <VendorMasterList />
        </>
      ),
    },
    {
      path: '/vendor-master/new',
      element: (
        <>
          <Helmet>
            <title>New Vendor</title>
          </Helmet>
          <VendorMasterDetail />
        </>
      ),
    },
    {
      path: '/vendor-master/view/:code',
      element: (
        <>
          <Helmet>
            <title>Vendor</title>
          </Helmet>
          <VendorMasterDetail />
        </>
      ),
    },
    {
      path: '/access-control',
      element: (
        <>
          <Helmet>
            <title>Access Control | ERPS</title>
          </Helmet>
          <AccessControl />
        </>
      ),
    },
    {
      path: '/sales/delivery-slots',
      element: (
        <>
          <Helmet>
            <title>Delivery Slots | ERPS</title>
          </Helmet>
          <DeliverySlots />
        </>
      ),
    },
    {
      path: '/config/transaction-setup',
      element: (
        <>
          <Helmet>
            <title>Transaction Setup | ERPS</title>
          </Helmet>
          <TxnSetupScreen />
        </>
      ),
    },
    {
      path: '/config/transaction-setup/:kind/:code',
      element: (
        <>
          <Helmet>
            <title>Transaction Setup | ERPS</title>
          </Helmet>
          <TxnSetupDetail />
        </>
      ),
    },
    {
      path: '/config/print-setup',
      element: (
        <>
          <Helmet>
            <title>Print Setup | ERPS</title>
          </Helmet>
          <PrintSetupScreen />
        </>
      ),
    },
    {
      path: '/config/keyboard-shortcuts',
      element: (
        <>
          <Helmet>
            <title>Keyboard Shortcuts | ERPS</title>
          </Helmet>
          <KeyboardShortcuts />
        </>
      ),
    },
    {
      path: '/bank-reconciliation',
      element: (
        <>
          <Helmet>
            <title>Bank Reconciliation | ERPS</title>
          </Helmet>
          <BankReconciliation />
        </>
      ),
    },
    {
      path: '/ecom-reconciliation',
      element: (
        <>
          <Helmet>
            <title>ECOM Reconciliation | ERPS</title>
          </Helmet>
          <EcomReconciliation />
        </>
      ),
    },
    {
      path: '/cheque-print',
      element: (
        <>
          <Helmet>
            <title>Cheque Printing | ERPS</title>
          </Helmet>
          <ChequePrintList />
        </>
      ),
    },
    {
      path: '/cheque-print/print/:id',
      element: (
        <>
          <Helmet>
            <title>Print Cheque</title>
          </Helmet>
          <ChequePrintView />
        </>
      ),
    },
    {
      path: '/cheque-print/print-manual',
      element: (
        <>
          <Helmet>
            <title>Print Manual Cheque</title>
          </Helmet>
          <ChequePrintView />
        </>
      ),
    },
    {
      path: '/new-listing',
      element: (
        <>
          <Helmet>
            <title>Vendors List</title>
          </Helmet>
          <SupplierList />
        </>
      ),
    },
    {
      path: '/new-listing/details',
      element: (
        <>
          <Helmet>
            <title>Vendors Details</title>
          </Helmet>
          <SupplierDetails />
        </>
      ),
    },
    {
      path: '/sales-plan/AddSalesPlan',
      element: (
        <>
          <Helmet>
            <title>Sales Plan</title>
          </Helmet>
          <SalesPlan />
        </>
      ),
    },
    {
      path: '/sales-plan/EditSalesPlan',
      element: (
        <>
          <Helmet>
            <title>Sales Plan</title>
          </Helmet>
          <SalesPlan />
        </>
      ),
    },
    {
      path: '/sales-plan/SalesPlanDetails',
      element: (
        <>
          <Helmet>
            <title>Sales Plan</title>
          </Helmet>
          <SalesPlan />
        </>
      ),
    },
    {
      path: '/sales-report',
      element: (
        <>
          <Helmet>
            <title>Sales Report | ERPS</title>
          </Helmet>
          <CustomerWiseSales />
        </>
      ),
    },
    {
      path: '/customer/excel-upload',
      element: (
        <>
          <Helmet>
            <title>Customer Item Upload | ERPS</title>
          </Helmet>
          <UploadCustomerItemEntry />
        </>
      ),
    },
    {
      path: '/customer-item-upload',
      element: (
        <>
          <Helmet>
            <title>Customer Item Upload</title>
          </Helmet>
          <UploadCustomerItemEntry />
        </>
      ),
    },
    {
      path: '/sales-plan',
      element: (
        <>
          <Helmet>
            <title>Sales Plans | ERPS</title>
          </Helmet>
          <SalesPlanList />
        </>
      ),
    },
    {
      path: '/target-sales/',
      element: (
        <>
          <Helmet>
            <title>Sales Targets | ERPS</title>
          </Helmet>
          <TargetSalesList />
        </>
      ),
    },
    {
      path: '/target-sales/Zone-target',
      element: (
        <>
          <Helmet>
            <title>Zone Target</title>
          </Helmet>
          <ZoneTarget />
        </>
      ),
    },
    {
      path: '*',
      element: (
        <>
          <Helmet>
            <title>Page Not Found</title>
          </Helmet>
          <NotFoundPage />
        </>
      ),
    },
    // {
    //   path: "/changePassword",
    //   element: (
    //     <>
    //       <Helmet>
    //         <title>Change Password</title>
    //       </Helmet>
    //       <ChangePasswordFromDashboard />
    //     </>
    //   ),
    // },
    // {
    //   path: "/changePin",
    //   element: (
    //     <>
    //       <Helmet>
    //         <title>Change Pin</title>
    //       </Helmet>
    //       <ChangePinFromDashboard />
    //     </>
    //   ),
    // },
  ],
};

export default MainRoutes;
